/* eslint-disable import/prefer-default-export, consistent-return */
import { useEffect } from 'react'

/**
 * Frontend tamper deterrent: blocks right-click, common devtools shortcuts,
 * text selection of sensitive elements, and drag-save of images.
 *
 * IMPORTANT — this is UX friction only, NOT a security control. A determined
 * user can always:
 *   - disable JavaScript
 *   - attach a remote debugger
 *   - use a proxy (Burp, mitmproxy) to read/modify traffic
 *   - use `chrome://inspect` or Safari Web Inspector
 *
 * Real protection lives server-side (authz, rate-limit, audit, WAF). Treat
 * this hook as a "keep honest people honest" measure and never rely on it to
 * hide secrets — never ship secrets to the browser in the first place.
 *
 * Disabled automatically in dev (NODE_ENV !== 'production') so engineers can
 * still work, and can be force-disabled with REACT_APP_DISABLE_DEVTOOLS_GUARD=true.
 */

const BLOCKED_KEY_COMBOS: Array<(e: KeyboardEvent) => boolean> = [
  // F12
  (e) => e.key === 'F12',
  // Ctrl/Cmd + Shift + I  (DevTools)
  (e) =>
    (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i'),
  // Ctrl/Cmd + Shift + J  (Console)
  (e) =>
    (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'J' || e.key === 'j'),
  // Ctrl/Cmd + Shift + C  (Inspect element)
  (e) =>
    (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'C' || e.key === 'c'),
  // Ctrl/Cmd + U  (View source)
  (e) =>
    (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'U' || e.key === 'u'),
  // Ctrl/Cmd + S  (Save page)
  (e) =>
    (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'S' || e.key === 's'),
  // Ctrl/Cmd + Shift + K  (Firefox console)
  (e) =>
    (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'K' || e.key === 'k')
]

export function useDevToolsGuard(): void {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (process.env.REACT_APP_DISABLE_DEVTOOLS_GUARD === 'true') return

    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      for (const match of BLOCKED_KEY_COMBOS) {
        if (match(e)) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
      }
    }

    const onDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'IMG' || target.tagName === 'A')) {
        e.preventDefault()
      }
    }

    // Strip any existing console output once so logs prior to guard are gone,
    // then neutralize the common console methods so casual onlookers see an
    // empty console. (Trivially reversible — again, deterrence only.)
    const neutralizedConsole = [
      'log',
      'debug',
      'info',
      'warn',
      'error',
      'table',
      'trace'
    ] as const
    const originals: Partial<
      Record<(typeof neutralizedConsole)[number], unknown>
    > = {}
    neutralizedConsole.forEach((method) => {
      originals[method] = (console as unknown as Record<string, unknown>)[
        method
      ]
      ;(console as unknown as Record<string, unknown>)[method] = () => undefined
    })
    try {
      // eslint-disable-next-line no-console
      console.clear?.()
    } catch {
      /* ignore */
    }

    window.addEventListener('contextmenu', onContextMenu)
    window.addEventListener('keydown', onKeyDown, { capture: true })
    window.addEventListener('dragstart', onDragStart)

    return () => {
      window.removeEventListener('contextmenu', onContextMenu)
      window.removeEventListener('keydown', onKeyDown, {
        capture: true
      } as EventListenerOptions)
      window.removeEventListener('dragstart', onDragStart)
      neutralizedConsole.forEach((method) => {
        if (originals[method]) {
          ;(console as unknown as Record<string, unknown>)[method] =
            originals[method]
        }
      })
    }
  }, [])
}
